import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2, Building2, Home, Map } from 'lucide-react';

// Lazy load MapPreview for performance
const MapPreview = lazy(() => import('./MapPreview'));

/**
 * Types de lieux pour un meilleur filtrage
 */
const PLACE_TYPES = {
  city: { icon: Building2, label: 'Ville', priority: 1 },
  town: { icon: Building2, label: 'Ville', priority: 2 },
  village: { icon: Home, label: 'Village', priority: 3 },
  suburb: { icon: MapPin, label: 'Quartier', priority: 4 },
  neighbourhood: { icon: MapPin, label: 'Quartier', priority: 5 },
  municipality: { icon: Building2, label: 'Commune', priority: 2 },
  administrative: { icon: Building2, label: 'Région', priority: 6 },
};

/**
 * Composant d'autocomplétion d'adresses utilisant OpenStreetMap Nominatim API
 * Filtré pour l'Afrique avec affichage optionnel de carte
 */
const AddressAutocomplete = ({
  value,
  onChange,
  placeholder = "Rechercher une ville...",
  onSelect,
  className = "",
  inputClassName = "",
  disabled = false,
  showMap = false,
  mapHeight = "180px",
  filterType = "all", // "all", "cities", "neighborhoods"
  "data-testid": dataTestId
}) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value
  useEffect(() => {
    if (value !== undefined && value !== query) {
      setQuery(value);
    }
  }, [value]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getPlaceType = (item) => {
    const type = item.type || item.addresstype;
    return PLACE_TYPES[type] || { icon: MapPin, label: 'Lieu', priority: 10 };
  };

  const filterByType = (items) => {
    if (filterType === "all") return items;
    
    return items.filter(item => {
      const type = item.type || item.addresstype;
      const cls = item.class;
      if (filterType === "cities") {
        // Include administrative for capitals and major cities
        return ['city', 'town', 'village', 'municipality', 'administrative', 'place'].includes(type) ||
               cls === 'place';
      }
      if (filterType === "neighborhoods") {
        return ['suburb', 'neighbourhood', 'residential', 'quarter', 'hamlet', 'locality'].includes(type);
      }
      return true;
    });
  };

  const searchAddress = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      // Nominatim API - filtered for Africa using viewbox
      // West: -18, East: 52, South: -35, North: 38
      const params = new URLSearchParams({
        q: searchQuery,
        format: 'json',
        addressdetails: '1',
        limit: '15',
        viewbox: '-18,-35,52,38',
        bounded: '0', // Allow results outside viewbox but prefer within
        'accept-language': 'fr'
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        {
          headers: {
            'User-Agent': 'DOCKHTA-HealthApp/1.0'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Filter to African countries only
        const africanCountries = [
          'Cameroun', 'Cameroon', 'Sénégal', 'Senegal', 'Côte d\'Ivoire', 'Ivory Coast',
          'Mali', 'Burkina Faso', 'Niger', 'Tchad', 'Chad', 'Nigeria', 'Ghana',
          'Togo', 'Bénin', 'Benin', 'Guinée', 'Guinea', 'Gabon', 'Congo',
          'République centrafricaine', 'Central African Republic', 'Kenya',
          'Tanzanie', 'Tanzania', 'Ouganda', 'Uganda', 'Rwanda', 'Burundi',
          'Éthiopie', 'Ethiopia', 'Maroc', 'Morocco', 'Algérie', 'Algeria',
          'Tunisie', 'Tunisia', 'Égypte', 'Egypt', 'Afrique du Sud', 'South Africa',
          'Mozambique', 'Zimbabwe', 'Zambie', 'Zambia', 'Angola', 'Namibie', 'Namibia',
          'Madagascar', 'Mauritanie', 'Mauritania', 'Liberia', 'Sierra Leone'
        ];
        
        // Filter by African countries and keep relevant place types
        let filtered = data.filter(item => {
          const country = item.address?.country || '';
          const isAfrican = africanCountries.some(c => 
            country.toLowerCase().includes(c.toLowerCase())
          );
          
          if (!isAfrican) return false;
          
          // Keep most place types for better results
          const type = item.type;
          const cls = item.class;
          return ['city', 'town', 'village', 'suburb', 'neighbourhood', 'municipality', 
                  'administrative', 'residential', 'quarter', 'hamlet', 'locality',
                  'place', 'boundary'].includes(type) ||
                 cls === 'place' || cls === 'boundary';
        });

        // Apply additional filter based on filterType prop
        filtered = filterByType(filtered);

        // Sort by priority (cities first, then towns, villages, neighborhoods)
        filtered.sort((a, b) => {
          const priorityA = getPlaceType(a).priority;
          const priorityB = getPlaceType(b).priority;
          return priorityA - priorityB;
        });

        // Deduplicate by similar names
        const seen = new Set();
        filtered = filtered.filter(item => {
          const key = `${item.address?.city || item.address?.town || item.address?.village || item.name}-${item.address?.country}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setSuggestions(filtered.slice(0, 8));
      }
    } catch (error) {
      console.error('Nominatim search error:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange && onChange(newValue);
    setShowSuggestions(true);

    // Debounce API calls
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchAddress(newValue);
    }, 300);
  };

  const handleSelect = (suggestion) => {
    const displayName = formatDisplayName(suggestion);
    const locationData = {
      display_name: displayName,
      lat: parseFloat(suggestion.lat),
      lon: parseFloat(suggestion.lon),
      address: suggestion.address,
      type: suggestion.type || suggestion.addresstype,
      raw: suggestion
    };
    
    setQuery(displayName);
    setSelectedLocation(locationData);
    onChange && onChange(displayName);
    onSelect && onSelect(locationData);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const formatDisplayName = (suggestion) => {
    const addr = suggestion.address || {};
    const parts = [];
    
    // Primary name (neighborhood/suburb or city)
    const primaryName = addr.suburb || addr.neighbourhood || addr.city || addr.town || addr.village || addr.municipality || suggestion.name;
    if (primaryName) parts.push(primaryName);
    
    // If we have a neighborhood, also show the city
    if ((addr.suburb || addr.neighbourhood) && (addr.city || addr.town)) {
      const cityName = addr.city || addr.town;
      if (cityName !== primaryName) parts.push(cityName);
    }
    
    // State/region (if different from city)
    if (addr.state && !parts.includes(addr.state)) {
      parts.push(addr.state);
    }
    
    // Country
    if (addr.country) parts.push(addr.country);
    
    return parts.join(', ') || suggestion.display_name;
  };

  const formatShortName = (suggestion) => {
    const addr = suggestion.address || {};
    return addr.suburb || addr.neighbourhood || addr.city || addr.town || addr.village || suggestion.name || '';
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          className={`pl-10 pr-10 ${inputClassName}`}
          disabled={disabled}
          data-testid={dataTestId}
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-3 w-4 h-4 text-slate-400 animate-spin" />
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {suggestions.map((suggestion, index) => {
            const placeType = getPlaceType(suggestion);
            const PlaceIcon = placeType.icon;
            return (
              <button
                key={`${suggestion.place_id}-${index}`}
                type="button"
                onClick={() => handleSelect(suggestion)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-start gap-3 border-b border-slate-100 last:border-0 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <PlaceIcon className="w-4 h-4 text-red-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 text-sm truncate">
                      {formatShortName(suggestion)}
                    </p>
                    <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-full flex-shrink-0">
                      {placeType.label}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">
                    {formatDisplayName(suggestion)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* No results */}
      {showSuggestions && query.length >= 2 && !loading && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-4">
          <p className="text-sm text-slate-500 text-center">
            Aucun résultat trouvé pour &quot;{query}&quot;
          </p>
          <p className="text-xs text-slate-400 text-center mt-1">
            Essayez un nom de ville ou de quartier
          </p>
        </div>
      )}

      {/* Map Preview */}
      {showMap && (
        <div className="mt-3">
          <Suspense fallback={
            <div className="h-[180px] bg-slate-100 rounded-lg flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          }>
            <MapPreview
              latitude={selectedLocation?.lat}
              longitude={selectedLocation?.lon}
              locationName={selectedLocation ? formatShortName(selectedLocation.raw) : ''}
              height={mapHeight}
              clickable={true}
              showGpsButton={true}
              showInstructions={!selectedLocation}
              onLocationSelect={(locationData) => {
                const newLocation = {
                  lat: locationData.lat,
                  lon: locationData.lng,
                  display_name: locationData.fullAddress || `${locationData.lat.toFixed(4)}, ${locationData.lng.toFixed(4)}`,
                  address: locationData.address,
                  raw: { address: locationData.address, name: locationData.locationName }
                };
                setSelectedLocation(newLocation);
                setQuery(locationData.locationName || locationData.fullAddress || '');
                onChange && onChange(locationData.locationName || locationData.fullAddress || '');
                onSelect && onSelect({
                  display_name: locationData.fullAddress,
                  lat: locationData.lat,
                  lon: locationData.lng,
                  address: locationData.address,
                  type: 'manual_selection',
                  raw: { address: locationData.address }
                });
              }}
            />
          </Suspense>
          {selectedLocation && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <Map className="w-3 h-3" />
              Coordonnées: {selectedLocation.lat.toFixed(4)}, {selectedLocation.lon.toFixed(4)}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
